import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Search, Loader2, CheckCircle, Users, Wallet, ChevronLeft, ChevronRight,
  AlertTriangle, ArrowRight, DollarSign, Check
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => `NGN ${Math.round(Number(n || 0)).toLocaleString()}`;

export default function BulkFundWallets({ loadEmployees, executeFunding, companyId }) {
  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [companyBalance, setCompanyBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 50;

  // Selection & amounts
  const [selected, setSelected] = useState(new Map()); // Map<emp_id, amount>
  const [bulkAmount, setBulkAmount] = useState('');
  const [step, setStep] = useState('select'); // select, confirm, result
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await loadEmployees({ search, page, page_size: pageSize });
      setEmployees(data.employees || []);
      setTotal(data.total || 0);
      setCompanyBalance(data.company_balance || 0);
    } catch { toast.error('Failed to load employees'); }
    finally { setLoading(false); }
  }, [loadEmployees, search, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const toggleSelect = (emp) => {
    setSelected(prev => {
      const next = new Map(prev);
      if (next.has(emp.id)) { next.delete(emp.id); }
      else { next.set(emp.id, parseFloat(bulkAmount) || 0); }
      return next;
    });
  };

  const selectAll = () => {
    const amt = parseFloat(bulkAmount) || 0;
    const next = new Map();
    employees.forEach(e => { if (e.has_wallet) next.set(e.id, amt); });
    setSelected(next);
  };

  const deselectAll = () => setSelected(new Map());

  const setIndividualAmount = (empId, amount) => {
    setSelected(prev => {
      const next = new Map(prev);
      next.set(empId, parseFloat(amount) || 0);
      return next;
    });
  };

  const applyBulkAmount = () => {
    const amt = parseFloat(bulkAmount) || 0;
    if (!amt) { toast.error('Enter an amount first'); return; }
    setSelected(prev => {
      const next = new Map(prev);
      for (const [id] of next) { next.set(id, amt); }
      return next;
    });
    toast.success(`Set ${fmt(amt)} for ${selected.size} selected`);
  };

  const totalFunding = Array.from(selected.values()).reduce((s, a) => s + a, 0);
  const selectedItems = employees.filter(e => selected.has(e.id));
  const allItems = [...selected.entries()].map(([id, amt]) => ({ employee_id: id, amount: amt })).filter(i => i.amount > 0);

  const handleExecute = async () => {
    if (allItems.length === 0) { toast.error('No valid items'); return; }
    setExecuting(true);
    try {
      const res = await executeFunding({
        company_id: companyId,
        items: allItems,
        description: `Bulk wallet funding - ${allItems.length} employees`,
      });
      setResult(res);
      setStep('result');
      toast.success(`Funded ${res.funded_count} wallets`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Funding failed');
    } finally { setExecuting(false); }
  };

  const totalPages = Math.ceil(total / pageSize);
  const allWithWallet = employees.filter(e => e.has_wallet);
  const allSelected = allWithWallet.length > 0 && allWithWallet.every(e => selected.has(e.id));

  // ── RESULT ──────────────────────────
  if (step === 'result' && result) {
    return (
      <div className="max-w-lg mx-auto py-8 space-y-5" data-testid="funding-result">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="w-8 h-8 text-emerald-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Funding Complete</h2>
          <p className="text-sm text-gray-500 mt-1">Batch: {result.batch_ref}</p>
        </div>
        <Card className="border-gray-200">
          <CardContent className="p-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Wallets Funded</span><span className="font-bold text-emerald-600">{result.funded_count}</span></div>
            {result.failed_count > 0 && <div className="flex justify-between"><span className="text-gray-500">Failed</span><span className="font-bold text-red-500">{result.failed_count}</span></div>}
            <div className="flex justify-between"><span className="text-gray-500">Total Funded</span><span className="font-bold text-gray-900">{fmt(result.total_funded)}</span></div>
            <div className="flex justify-between border-t pt-2"><span className="text-gray-500">Company New Balance</span><span className="font-bold text-gray-900">{fmt(result.company_new_balance)}</span></div>
          </CardContent>
        </Card>
        {result.failed && result.failed.length > 0 && (
          <Card className="border-red-200">
            <CardContent className="p-3">
              <p className="text-xs font-semibold text-red-600 mb-2">Failed Items</p>
              {result.failed.map((f, i) => (
                <p key={i} className="text-xs text-red-500">{f.name || f.employee_id}: {f.reason}</p>
              ))}
            </CardContent>
          </Card>
        )}
        <Button className="w-full" onClick={() => { setStep('select'); setSelected(new Map()); setResult(null); load(); }} data-testid="fund-again-btn">
          Fund More Wallets
        </Button>
      </div>
    );
  }

  // ── CONFIRM ─────────────────────────
  if (step === 'confirm') {
    return (
      <div className="space-y-4" data-testid="funding-confirm">
        <button onClick={() => setStep('select')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ChevronLeft className="w-4 h-4" /> Back to selection
        </button>
        <h2 className="text-lg font-bold text-gray-900">Confirm Bulk Funding</h2>
        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
          <p className="text-xs text-amber-700">This will debit <strong>{fmt(totalFunding)}</strong> from the company wallet and credit <strong>{allItems.length}</strong> employee wallets. This action cannot be undone.</p>
        </div>
        <Card className="border-gray-200">
          <CardContent className="p-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">Company Balance</span><span className="font-bold">{fmt(companyBalance)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Total to Fund</span><span className="font-bold text-pink-600">{fmt(totalFunding)}</span></div>
            <div className="flex justify-between border-t pt-2"><span className="text-gray-500">Balance After</span><span className="font-bold">{fmt(companyBalance - totalFunding)}</span></div>
          </CardContent>
        </Card>
        <div className="max-h-48 overflow-y-auto border rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 sticky top-0">
              <tr><th className="text-left px-3 py-2 font-medium text-gray-500">Employee</th><th className="text-right px-3 py-2 font-medium text-gray-500">Amount</th></tr>
            </thead>
            <tbody>
              {allItems.map(item => {
                const emp = employees.find(e => e.id === item.employee_id);
                return (
                  <tr key={item.employee_id} className="border-t">
                    <td className="px-3 py-1.5 text-gray-900">{emp ? `${emp.first_name} ${emp.last_name}` : item.employee_id}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-gray-900">{fmt(item.amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Button className="w-full bg-pink-500 hover:bg-pink-600 text-white h-11" disabled={executing || totalFunding > companyBalance} onClick={handleExecute} data-testid="execute-funding-btn">
          {executing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Confirm & Fund {fmt(totalFunding)}
        </Button>
      </div>
    );
  }

  // ── SELECT ──────────────────────────
  return (
    <div className="space-y-4" data-testid="funding-select">
      {/* Summary bar */}
      <div className="flex flex-wrap items-center gap-3">
        <Card className="border-gray-200 flex-1 min-w-[160px]">
          <CardContent className="p-3 flex items-center gap-2">
            <Wallet className="w-4 h-4 text-blue-500" />
            <div><p className="text-[10px] text-gray-400">Company Balance</p><p className="text-sm font-bold" style={{ fontFamily: 'JetBrains Mono' }}>{fmt(companyBalance)}</p></div>
          </CardContent>
        </Card>
        <Card className="border-gray-200 flex-1 min-w-[120px]">
          <CardContent className="p-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-pink-500" />
            <div><p className="text-[10px] text-gray-400">Total Employees</p><p className="text-sm font-bold">{total}</p></div>
          </CardContent>
        </Card>
        <Card className={`border-gray-200 flex-1 min-w-[120px] ${selected.size > 0 ? 'border-pink-300 bg-pink-50/50' : ''}`}>
          <CardContent className="p-3 flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-500" />
            <div><p className="text-[10px] text-gray-400">Selected</p><p className="text-sm font-bold">{selected.size}</p></div>
          </CardContent>
        </Card>
        {totalFunding > 0 && (
          <Card className="border-pink-300 bg-pink-50/50 flex-1 min-w-[160px]">
            <CardContent className="p-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-pink-500" />
              <div><p className="text-[10px] text-gray-400">Total to Fund</p><p className="text-sm font-bold text-pink-600" style={{ fontFamily: 'JetBrains Mono' }}>{fmt(totalFunding)}</p></div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[180px] max-w-xs space-y-1.5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
              placeholder="Search employees..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-300"
              data-testid="fund-search" />
          </div>
          <Button size="sm" variant="outline" className="h-7 text-xs w-full" onClick={allSelected ? deselectAll : selectAll}
            data-testid="select-all-btn">{allSelected ? 'Deselect All' : `Select All (${allWithWallet.length})`}</Button>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-[10px] text-gray-500 font-medium block mb-1">Amount per person</label>
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">NGN</span>
              <Input type="text" inputMode="numeric" value={bulkAmount}
                onChange={e => setBulkAmount(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="0" className="h-9 pl-10 w-32 text-sm font-bold" style={{ fontFamily: 'JetBrains Mono' }}
                data-testid="bulk-amount-input" />
            </div>
          </div>
          <Button size="sm" variant="outline" className="h-9 text-xs" onClick={applyBulkAmount} disabled={selected.size === 0}
            data-testid="apply-amount-btn">Apply to Selected</Button>
        </div>
      </div>

      {/* Employee list */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-pink-400" /></div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-10 px-3 py-2.5"></th>
                <th className="text-left px-3 py-2.5 font-medium text-gray-500">Employee</th>
                <th className="text-left px-3 py-2.5 font-medium text-gray-500 hidden sm:table-cell">Department</th>
                <th className="text-right px-3 py-2.5 font-medium text-gray-500">Wallet Balance</th>
                <th className="text-right px-3 py-2.5 font-medium text-gray-500 w-36">Fund Amount</th>
              </tr>
            </thead>
            <tbody>
              {employees.map(emp => {
                const isSelected = selected.has(emp.id);
                const amount = selected.get(emp.id) || 0;
                return (
                  <tr key={emp.id} className={`border-t transition ${isSelected ? 'bg-pink-50/50' : 'hover:bg-gray-50'} ${!emp.has_wallet ? 'opacity-50' : ''}`}
                    data-testid={`fund-row-${emp.id}`}>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => emp.has_wallet && toggleSelect(emp)}
                        disabled={!emp.has_wallet}
                        className={`w-5 h-5 rounded border flex items-center justify-center transition ${isSelected ? 'bg-pink-500 border-pink-500 text-white' : 'border-gray-300 hover:border-pink-300'}`}
                        data-testid={`fund-check-${emp.id}`}>
                        {isSelected && <Check className="w-3 h-3" />}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <p className="font-medium text-gray-900">{emp.first_name} {emp.last_name}</p>
                      <p className="text-[10px] text-gray-400">{emp.email}</p>
                    </td>
                    <td className="px-3 py-2 hidden sm:table-cell text-gray-600">{emp.department || '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-gray-900">{emp.has_wallet ? fmt(emp.wallet_balance) : 'No wallet'}</td>
                    <td className="px-3 py-2 text-right">
                      {isSelected ? (
                        <Input type="text" inputMode="numeric"
                          value={amount || ''}
                          onChange={e => setIndividualAmount(emp.id, e.target.value.replace(/[^0-9]/g, ''))}
                          className="h-7 w-28 text-xs text-right font-mono ml-auto"
                          data-testid={`fund-amount-${emp.id}`} />
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} of {total}</span>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="w-3 h-3" />
          </Button>
          <span className="px-2 py-1">{page + 1}/{totalPages || 1}</span>
          <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Fund button */}
      {selected.size > 0 && (
        <div className="sticky bottom-0 bg-white border-t pt-3 pb-1 flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-gray-900">{selected.size} employees selected</p>
            <p className="text-xs text-gray-500">Total: <span className="font-bold text-pink-600">{fmt(totalFunding)}</span></p>
          </div>
          <Button className="bg-pink-500 hover:bg-pink-600 text-white h-10 px-6"
            disabled={totalFunding <= 0 || totalFunding > companyBalance || allItems.length === 0}
            onClick={() => setStep('confirm')}
            data-testid="proceed-funding-btn">
            <ArrowRight className="w-4 h-4 mr-2" /> Proceed to Fund
          </Button>
        </div>
      )}
    </div>
  );
}
